import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { getUserToken } from '@/redux/user/user.slice';
import {
    useGetVatConfigurationQuery,
    useLazyGetVatConfigurationCsvQuery,
} from '@/services/innovorder/vatConfiguration/vatConfiguration.endpoints';
import type {
    OperationZoneDto,
    VatGroupWithRatesDto,
} from '@/services/innovorder/vatConfiguration/vatConfiguration.type';
import { downloadFile } from '@/utils/file/utils';

export const COUNTRY_FLAG_MAP: Record<string, string> = {
    FR: '\u{1F1EB}\u{1F1F7}',
    IT: '\u{1F1EE}\u{1F1F9}',
    ES: '\u{1F1EA}\u{1F1F8}',
    LU: '\u{1F1F1}\u{1F1FA}',
    BE: '\u{1F1E7}\u{1F1EA}',
    DE: '\u{1F1E9}\u{1F1EA}',
    PT: '\u{1F1F5}\u{1F1F9}',
    NL: '\u{1F1F3}\u{1F1F1}',
    GB: '\u{1F1EC}\u{1F1E7}',
    CH: '\u{1F1E8}\u{1F1ED}',
};

export type ConsumptionMode = 'eatIn' | 'takeAway' | 'delivery';

export const useVatRatesMatrixVM = () => {
    const token = useSelector(getUserToken);
    const { search } = useLocation();
    const { data, isLoading, error } = useGetVatConfigurationQuery({ token });

    const zoneFromUrl = useMemo(() => new URLSearchParams(search).get('zone') ?? '', [search]);

    const [zoneFilter, setZoneFilter] = useState<string>(zoneFromUrl);
    const [groupFilter, setGroupFilter] = useState<string>('');
    const [modeFilter, setModeFilter] = useState<ConsumptionMode | ''>('');

    // Keep filter in sync with URL: if user lands on the page (or navigates to it)
    // with a ?zone=XX param, apply that filter.
    useEffect(() => {
        setZoneFilter(zoneFromUrl);
    }, [zoneFromUrl]);

    const filteredZones: OperationZoneDto[] = useMemo(() => {
        if (!data) return [];
        if (!zoneFilter) return data.operationZones;
        return data.operationZones.filter((z) => z.code === zoneFilter);
    }, [data, zoneFilter]);

    const filteredGroups: VatGroupWithRatesDto[] = useMemo(() => {
        if (!data) return [];
        if (!groupFilter) return data.vatGroups;
        return data.vatGroups.filter((g) => g.code === groupFilter);
    }, [data, groupFilter]);

    const uniqueZoneCodes = useMemo(() => {
        if (!data) return [];
        const seen = new Set<string>();
        return data.operationZones.filter((z) => {
            if (seen.has(z.code)) return false;
            seen.add(z.code);
            return true;
        });
    }, [data]);

    const [getVatConfigurationCsv, { error: exportError }] = useLazyGetVatConfigurationCsvQuery();

    const exportCsv = useCallback(async () => {
        if (!token) return;
        try {
            const result = await getVatConfigurationCsv({
                token,
                zoneCode: zoneFilter || undefined,
                groupCode: groupFilter || undefined,
                mode: modeFilter || undefined,
            }).unwrap();
            downloadFile('vat-configuration.csv', result, 'text/csv');
        } catch {
            // Error exposed via exportError state
        }
    }, [token, zoneFilter, groupFilter, modeFilter, getVatConfigurationCsv]);

    return {
        isLoading,
        error,
        operationZones: filteredZones,
        vatGroups: filteredGroups,
        uniqueZoneCodes,
        allGroups: data?.vatGroups ?? [],
        zoneFilter,
        setZoneFilter,
        groupFilter,
        setGroupFilter,
        modeFilter,
        setModeFilter,
        exportCsv,
        exportError,
    };
};
